import { IndexSort } from "@omni/types/api/search/algolia/shared/enum/indexSort.enum";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use } from "react";

import { useThunkDispatch } from "@/redux/dispatcher";
import { configureSearchComponent } from "@/redux/modules/value/search/search.actions";
import { useSearchComponentStateSelector } from "@/redux/modules/value/search/search.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { getInvalidationsForComponent } from "@/utils/search/invalidation.utils";
import { SearchComponentOfType } from "@/utils/search/types/searchComponent";

import { productSortByConfiguration } from "../sort.config";
import { SortItem } from "../sort.types";

type ProductSortContextValue = {
    onRefine: (value: IndexSort) => void;
    refinedItem: SortItem | undefined;
    items: Array<SortItem>;
};

type ProductSortContextProviderProps = RequireChildrenFor<{
    component: SearchComponentOfType<"SORT">;
}>;

export const ProductSortContext = createContext<
    ProductSortContextValue | undefined
>(undefined);
ProductSortContext.displayName = "ProductSortContext";

export const ProductSortContextProvider = ({
    children,
    component
}: ProductSortContextProviderProps) => {
    const { t } = useGranite();

    const dispatch = useThunkDispatch();
    const sortComponentState = useSearchComponentStateSelector(component);

    const configuration = productSortByConfiguration.filter(
        (cfg) => !cfg.isDisabled?.()
    );
    const defaultSort = configuration.find((sortBy) => sortBy.default)?.value;
    const refinedSort = sortComponentState?.sortBy ?? defaultSort;

    const items: Array<SortItem> = configuration.map((sort) => ({
        label: t(sort.tkLabel),
        value: sort.value,
        isRefined: sort.value === refinedSort
    }));
    const refinedItem = items.find((sortBy) => sortBy.isRefined);

    const handleOnRefine = (value: IndexSort) => {
        dispatch(
            configureSearchComponent(
                {
                    component: component,
                    sortBy: value
                },
                getInvalidationsForComponent(component)
            )
        );
    };
    return (
        <ProductSortContext
            value={{
                onRefine: handleOnRefine,
                refinedItem: refinedItem,
                items: items
            }}
        >
            {children}
        </ProductSortContext>
    );
};

export const useProductSortContext = () => {
    const context = use<ProductSortContextValue | undefined>(
        ProductSortContext
    );

    if (!isDefined(context)) {
        throw new Error(`${ProductSortContext.displayName} is undefined.`);
    }

    return context;
};
